import { useEffect, useRef, useState } from 'react';

/* ISO 8601 format */
export type ExpiryTime = string | undefined;

interface UseExpiryTime {
    timeLeft: number | undefined;
    timerStarted: boolean;
}

export const getReadableTime = (timeLeftMs: number): string => {
    if (timeLeftMs <= 0) {
        return '00:00';
    }

    const seconds = timeLeftMs / 1000;
    const m = Math.floor((seconds % 3600) / 60)
        .toString()
        .padStart(2, '0');
    const s = Math.floor(seconds % 60)
        .toString()
        .padStart(2, '0');

    return `${m}:${s}`;
};

export const getTimeLeft = (isoTimestamp: ExpiryTime): number => {
    const now = new Date();
    const targetTime = new Date(isoTimestamp || '');
    const timeLeftMs = targetTime.getTime() - now.getTime();

    if (Number.isNaN(timeLeftMs)) {
        return 0;
    }

    return timeLeftMs;
};

export function useExpiryTime(expiryTime: ExpiryTime, callback?: () => void): UseExpiryTime {
    const [timeLeft, setTimeLeft] = useState(getTimeLeft(expiryTime));
    const timerStarted = useRef(false);
    useEffect(() => {
        const timer = setInterval(() => {
            const currentTimeLeft = getTimeLeft(expiryTime);
            if (callback) {
                callback();
            }

            timerStarted.current = true;
            setTimeLeft(currentTimeLeft);
            if (currentTimeLeft <= 0) {
                clearInterval(timer);
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [callback, expiryTime]);

    return { timeLeft: expiryTime ? timeLeft : undefined, timerStarted: timerStarted.current };
}

export default useExpiryTime;
